/**
 * The Acknowledge stage (plan docs/plans/lms-plan.md 7.3, 8.1, 11.3, R20,
 * R25; WP-40): for a read_ack course (an SOP or a policy) and for an
 * acknowledge-mode assignment (a new version published with "Acknowledge the
 * change"). The learner reads the change summary and the document, marks the
 * reading done, then signs the read_ack statement with the credential
 * re-check: POST /courses/:id/acknowledge {password, attest: true}.
 *
 * The server refuses the acknowledgement until reading_done (11.3), answers
 * 401 LMS_REAUTH_FAILED for a wrong password (the dialog shows it and nothing
 * is signed), and 409 LMS_ALREADY_CURRENT when the person holds a current
 * completion and no open assignment (the learner may sign again anyway).
 */
import { useState } from 'react';
import { FileText, Lock } from 'lucide-react';
import { apiErrorCode, apiErrorMessage } from '../../api/client';
import { acknowledgeCourse } from '../../api/completions';
import type { CompletionResult } from '../../api/completions';
import { outlineBtnCls, primaryBtnCls } from '../../../ui/styles';
import { OpenSopButton } from '../../tabs/sops/OpenSopButton';
import { ReauthDialog } from './AttestDialog';
import { headingCls, microLabelCls, noticeCls, panelCls, stageCls } from './styles';

export interface AcknowledgeStageProps {
  courseId: string;
  courseTitle: string;
  readingDone: boolean;
  changeSummary: string | null;
  versionNo: number;
  signerName: string | null;
  signerEmail: string | null;
  onGoToReading: () => void;
  onCompleted: (result: CompletionResult) => void;
  /** 11.3, plan 18.27 (WP-46): an SOP course (category sop) shows "Open the SOP" before signing. */
  courseCategory?: string | null;
  courseVersionId?: string | null;
  /** The SOP file was opened from this stage (the player keeps one flag for the reading stage's gate). */
  onFileOpened?: () => void;
}

/** The 401 copy (7.3): the player says it in its own words, whatever the server's text. */
export const REAUTH_FAILED_MESSAGE = 'Your password could not be verified, so nothing was signed. The failed attempt has been recorded.';

export function AcknowledgeStage({
  courseId,
  courseTitle,
  readingDone,
  changeSummary,
  versionNo,
  signerName,
  signerEmail,
  onGoToReading,
  onCompleted,
  courseCategory = null,
  courseVersionId = null,
  onFileOpened,
}: AcknowledgeStageProps) {
  const [open, setOpen] = useState(false);
  const [force, setForce] = useState(false);
  const [alreadyCurrent, setAlreadyCurrent] = useState(false);

  const sign = async (password: string): Promise<string | null> => {
    try {
      const out = await acknowledgeCourse(courseId, password, { force });
      setOpen(false);
      onCompleted(out);
      return null;
    } catch (err) {
      switch (apiErrorCode(err)) {
        case 'LMS_REAUTH_FAILED':
          return REAUTH_FAILED_MESSAGE;
        case 'LMS_ALREADY_CURRENT':
          setOpen(false);
          setAlreadyCurrent(true);
          return null;
        case 'LMS_READING_REQUIRED':
          return 'Open the reading and mark it as read before you acknowledge.';
        case 'LMS_ACKNOWLEDGE_NOT_ALLOWED':
          return 'This course is completed with its quiz, not with an acknowledgement.';
        case 'LMS_NOT_PUBLISHED':
          return 'This course has no published version yet.';
        default:
          return apiErrorMessage(err, 'Your acknowledgement could not be recorded. Try again.');
      }
    }
  };

  return (
    <div className={stageCls} data-testid="lms-acknowledge-stage">
      <div className={`${panelCls} space-y-4`}>
        <h2 className={headingCls}>Acknowledge</h2>
        {changeSummary && (
          <div className="space-y-1" data-testid="lms-ack-change-summary">
            <div className={microLabelCls}>{`What changed in version ${versionNo}`}</div>
            <p className="whitespace-pre-line text-sm text-gray-700 dark:text-gray-300">{changeSummary}</p>
          </div>
        )}
        <p className="text-sm text-gray-700 dark:text-gray-300">
          {`Sign to record that you have read ${courseTitle} in full. Signing asks for your password again.`}
        </p>
        {!readingDone ? (
          <div className={noticeCls('amber')} data-testid="lms-ack-lock">
            <Lock className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <div className="min-w-0 space-y-2">
              <div>Open the reading and mark it as read first.</div>
              <button type="button" className={outlineBtnCls()} onClick={onGoToReading}>
                <FileText className="h-4 w-4" aria-hidden="true" />
                Go to the reading
              </button>
            </div>
          </div>
        ) : null}
        {alreadyCurrent && (
          <div className={noticeCls('amber')} role="alert" data-testid="lms-ack-already-current">
            <div className="min-w-0 space-y-2">
              <div>You already hold a current completion of this course.</div>
              <button
                type="button"
                className={outlineBtnCls()}
                onClick={() => {
                  setForce(true);
                  setAlreadyCurrent(false);
                  setOpen(true);
                }}
              >
                Sign again anyway
              </button>
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {courseCategory === 'sop' && <OpenSopButton courseId={courseId} courseVersionId={courseVersionId} onOpened={onFileOpened} />}
          <button
            type="button"
            className={primaryBtnCls}
            disabled={!readingDone}
            onClick={() => setOpen(true)}
            data-testid="lms-ack-open"
          >
            Acknowledge and sign
          </button>
        </div>
      </div>
      <ReauthDialog
        open={open}
        title="Sign your acknowledgement"
        signerName={signerName}
        signerEmail={signerEmail}
        intro={`${courseTitle}, version ${versionNo}`}
        onSign={sign}
        onClose={() => setOpen(false)}
      />
    </div>
  );
}

export default AcknowledgeStage;
